import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BaseService} from "@6grain/masofa-monolith-api-client/dictionaries/api.base.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {ServerTranslateService} from "@services/server-translate.service";
import {getDeepField} from "@app/utils/_msf";
import {CommonModule} from "@angular/common";
import {CheckboxComponent} from "@components/checkbox/checkbox.component";

@Component({
  selector: 'msf-multi-select',
  exportAs: 'msfMultiSelect',
  imports: [
    CommonModule,
    FormsModule,
    CheckboxComponent
  ],
  templateUrl: './multi-select.html',
  styleUrl: './multi-select.scss',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MsfMultiSelect),
      multi: true
    }
  ],
})
export class MsfMultiSelect implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  $destroy$: Subject<void> = new Subject<void>();

  orderedOptions: any[] = [];
  filteredOptions: any[] = [];
  loading: boolean = false;
  isDropdownOpen: boolean = false;
  filterValue: string = '';

  constructor(
    private serverTranslateService: ServerTranslateService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  private onTouchedCallback: () => void = () => {
  };
  private onChangeCallback: (_: any) => void = () => {
  };

  writeValue(obj: any): void {
    this.model = Array.isArray(obj) ? [...obj] : [];
    this.refreshOptionSelectionState();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @Input() options?: any[] = [];
  @Input() model: any[] = [];
  @Output() modelChange = new EventEmitter<any[]>();
  @Output() selectItem = new EventEmitter<any[]>();
  @Input() placeholder?: string = 'Выберите...';
  @Input() optionLabel: string = 'label';
  @Input() translateBy: string | null = 'names';
  @Input() optionValue?: string = 'id';
  @Input() service?: BaseService | any;
  @Input() serviceMethod?: string;
  @Input() showClear = false;
  @Input() filter = {};
  @Input() width = '100%';
  @Input() disabled: boolean = false;
  @Input() maxSelectedLabels: number = 3;
  @Input() maxSelectedItems?: number;
  @Input() selectedItemsLabel?: string = '{0} выбрано';
  @Input() filterPlaceholder?: string = 'Поиск...';
  @Input() panelStyleClass?: string;
  @Input() styleClass?: string;

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.msf-multi-select-wrapper')) {
      this.isDropdownOpen = false;
    }
  }

  ngOnInit() {
    if (this.service && this.serviceMethod) {
      this.loading = true;
      this.service[this.serviceMethod](this.filter || {})
        .pipe(takeUntil(this.$destroy$))
        .subscribe((data: any) => {
          this.options = data;
          this.refreshOptionSelectionState();
          this.loading = false;
        });
    } else {
      this.refreshOptionSelectionState();
    }

    this.translateService
      .onLangChange
      .pipe(takeUntil(this.$destroy$))
      .subscribe(() => {
        this.refreshOptionSelectionState();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.$destroy$.next();
    this.$destroy$.complete();
  }

  translatedOptions(): any[] {
    if (!this.options || !Array.isArray(this.options)) {
      return [];
    }

    return this.options
      .map((item: any) => {
        let labelValue: any;
        if (this.translateBy && this.translateBy === 'locale') {
          labelValue = this.translateService.instant(item[this.optionLabel]);
        } else if (this.translateBy && this.translateBy !== 'none') {
          labelValue = this.serverTranslateService.translate(item, this.translateBy);
        } else {
          labelValue = getDeepField(item, this.optionLabel);
        }

        const value = getDeepField(item, this.optionValue as string);
        const label = this.translateBy === 'locale'
          ? (labelValue?.toString() || '')
          : this.serverTranslateService.formatTranslationLabel(labelValue);

        return {
          ...item,
          [this.optionLabel]: label,
          _selected: this.model?.includes(value) || false,
        };
      })
      .filter((item: any) => {
        const hasValidLabel = this.serverTranslateService.isValidDisplayLabel(item.label);
        const namesNull = this.translateBy === 'names' && item.names
          && this.serverTranslateService.isEmptyTranslationValue(item.names);
        return hasValidLabel && !namesNull;
      })
      .slice()
      .sort((a, b) => {
        const labelA = (getDeepField(a, this.optionLabel) || '').toString().toLowerCase();
        const labelB = (getDeepField(b, this.optionLabel) || '').toString().toLowerCase();
        return labelA.localeCompare(labelB);
      })
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  }

  toggleOption(option: any) {
    if (this.disabled) return;

    const value = getDeepField(option, this.optionValue as string);
    const index = this.model.indexOf(value);

    if (index > -1) {
      // Remove if already selected
      this.model.splice(index, 1);
      option._selected = false;
    } else {
      // Add only if not at max limit (check maxSelectedItems, not maxSelectedLabels)
      if (!this.maxSelectedItems || this.model.length < this.maxSelectedItems) {
        this.model.push(value);
        option._selected = true;
      } else {
        // Limit reached, don't add
        return;
      }
    }

    this.modelChange.emit(this.model);
    this.onSelectionChange();
  }

  /**
   * Check if the selection limit has been reached
   */
  isMaxSelectionReached(): boolean {
    return this.maxSelectedItems !== undefined && this.model.length >= this.maxSelectedItems;
  }

  /**
   * Check if an option is disabled (can't be selected)
   */
  isOptionDisabled(option: any): boolean {
    // An option is disabled if it's not selected and we've reached the max limit
    const value = getDeepField(option, this.optionValue as string);
    const isSelected = this.model?.includes(value) || false;
    return !isSelected && this.isMaxSelectionReached();
  }

  isSelected(option: any): boolean {
    const value = getDeepField(option, this.optionValue as string);
    return this.model?.includes(value) || false;
  }

  getSelectedItems(): any[] {
    return this.orderedOptions.filter(option =>
      this.model?.includes(getDeepField(option, this.optionValue as string))
    );
  }

  getSelectedItemsLabel(): string {
    const count = this.model?.length || 0;
    return `${count} ${this.translateService.instant('UPLOAD_IMAGES.SELECTED_COUNT')}`;
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.model = [];

    // Reset _selected property for all options
    this.orderedOptions.forEach(option => {
      option._selected = false;
    });
    this.filteredOptions.forEach(option => {
      option._selected = false;
    });

    this.modelChange.emit(this.model);
    this.onSelectionChange();
  }

  onFilterChange() {
    const searchTerm = this.filterValue.toLowerCase().trim();
    if (!searchTerm) {
      this.filteredOptions = [...this.orderedOptions];
    } else {
      this.filteredOptions = this.orderedOptions.filter(option => {
        const label = (option[this.optionLabel] || '').toString().toLowerCase();
        return label.includes(searchTerm);
      });
    }
  }

  onSelectionChange() {
    const selectedItems = this.options?.filter(i =>
      this.model?.includes(getDeepField(i, this.optionValue as string))
    ) || [];
    this.selectItem.emit(selectedItems);
    this.onChangeCallback(this.model);
    this.onTouchedCallback();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['model'] || changes['translateBy']) {
      this.refreshOptionSelectionState();
    }
  }

  private refreshOptionSelectionState(): void {
    this.orderedOptions = this.translatedOptions();
    this.filteredOptions = [...this.orderedOptions];
  }
}
